import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Grid2 as Grid, Button,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Alert, Chip,
} from '@mui/material';
import { Receipt as TxIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { posAdminService, PosTransacao } from '../../services/pos-admin.service';
import { dayCv, todayCv } from '../../utils/date';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;

const ESTADO_COR: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  PAID: 'success', PENDING: 'warning', EXPIRED: 'default', CANCELLED: 'error',
};

const ESTADO_LABEL: Record<string, string> = {
  PAID: 'Pago', PENDING: 'Pendente', EXPIRED: 'Expirado', CANCELLED: 'Cancelado',
};

/**
 * Pedidos de pagamento gerados nos terminais.
 *
 * Os carregamentos em numerário não aparecem aqui — vivem no seu próprio
 * registo, em Relatórios → Numerário Recebido.
 */
export default function PosTransacoes() {
  const hoje = todayCv();
  const ha30 = dayCv(new Date(Date.now() - 30 * 864e5));

  const [linhas, setLinhas] = useState<PosTransacao[]>([]);
  const [totais, setTotais] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [from, setFrom] = useState(ha30);
  const [to, setTo] = useState(hoje);
  const [estado, setEstado] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await posAdminService.transacoes({
        from, to, estado: estado || undefined, page: page + 1, limit,
      });
      setLinhas(r.linhas);
      setTotais(r.totais);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível carregar as transacções.');
    } finally {
      setLoading(false);
    }
  }, [from, to, estado, page, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <TxIcon color="primary" /> Transacções POS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pedidos de pagamento gerados nos terminais. O numerário está em Relatórios → Numerário Recebido.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {totais && (
        <Grid container spacing={2}>
          {[
            { l: 'Pedidos', v: String(totais.pedidos) },
            { l: 'Montante pago', v: cve(totais.montantePago), d: true },
            { l: 'Pagos', v: String(totais.pagos) },
            { l: 'Pendentes', v: String(totais.pendentes) },
            { l: 'Expirados', v: String(totais.expirados) },
          ].map((c) => (
            <Grid key={c.l} size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Card variant="outlined" sx={c.d ? { bgcolor: 'primary.50' } : undefined}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">{c.l}</Typography>
                  <Typography variant="h6" fontWeight={700}
                    color={c.d ? 'primary.main' : 'text.primary'}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {c.v}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
                value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
                value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" select label="Estado"
                value={estado} onChange={(e) => { setEstado(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="PAID">Pago</MenuItem>
                <MenuItem value="PENDING">Pendente</MenuItem>
                <MenuItem value="EXPIRED">Expirado</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : linhas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Sem transacções no período.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Referência</TableCell>
                    <TableCell>Terminal</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Montante</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {new Date(t.criadoEm).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{t.referencia}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{t.terminalNome ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{t.descricao ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {cve(t.montante)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={ESTADO_LABEL[t.estado] ?? t.estado}
                          color={ESTADO_COR[t.estado] ?? 'default'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totais?.pedidos ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Linhas"
          />
        </CardContent>
      </Card>
    </Box>
  );
}
